import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ShieldCheck } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1758216434916-0819f9cd3a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGVsZWdhbnQlMjBiYWNrZ3JvdW5kJTIwYmVpZ2V8ZW58MHx8fHwxNzc4MTU4ODI4fDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (user && user !== false) {
      const dest = loc.state?.from?.pathname || "/";
      nav(dest, { replace: true });
    }
  }, [user, nav, loc]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white"
           style={{ backgroundImage: `linear-gradient(135deg, rgba(44,85,69,0.85), rgba(28,28,27,0.55)), url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-white/10 backdrop-blur grid place-items-center">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div className="font-display font-semibold tracking-tight text-lg">KREPP</div>
        </div>
        <div className="max-w-md">
          <div className="label mb-4 text-white/70">FINANCIAL PLATFORM</div>
          <h1 className="font-display font-light text-4xl xl:text-5xl leading-tight mb-6">
            A grounded platform for people who lend with care.
          </h1>
          <p className="text-white/80 leading-relaxed">
            Manage savings, loans, repayments, and reporting — securely, with full data encryption.
          </p>
        </div>
        <div className="text-white/60 text-xs tracking-[0.2em] uppercase">Secure · Intelligent · Smart</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-forest grid place-items-center text-white">
              <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="font-display font-bold text-lg">KREPP</div>
          </div>
          <div className="label mb-2">Welcome back</div>
          <h2 className="font-display text-3xl font-semibold tracking-tight mb-8">Sign in to your account</h2>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label block mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                     className="w-full bg-white border border-border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                     data-testid="login-email-input" autoComplete="email" />
            </div>
            <div>
              <label className="label block mb-2">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                     className="w-full bg-white border border-border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                     data-testid="login-password-input" autoComplete="current-password" />
            </div>
            {err && <div className="text-sm text-secondary" data-testid="login-error">{err}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full py-3" data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 text-sm text-muted-foreground flex items-center justify-between">
            <Link to="/forgot-password" className="text-forest hover:underline" data-testid="forgot-link">
              Forgot password?
            </Link>
            <Link to="/register" className="text-forest font-medium hover:underline" data-testid="register-link">
              Create account →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
